import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CardHeader, Grid, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import PasswordStrengthIndicator from '../PasswordStrengthIndicator';
import { calculatePasswordStrength } from '../../utils/passwordValidator';

type Feedback = { severity: 'success' | 'error'; message: string } | null;

const apiError = (err: any, fallback: string) => err?.response?.data?.error || fallback;

const ProfilePage: React.FC = () => {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState(user?.name || '');
    const [profileFeedback, setProfileFeedback] = useState<Feedback>(null);
    const [savingProfile, setSavingProfile] = useState(false);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordFeedback, setPasswordFeedback] = useState<Feedback>(null);
    const [savingPassword, setSavingPassword] = useState(false);

    if (!user) return null;

    const handleSaveProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSavingProfile(true);
            setProfileFeedback(null);
            const response = await api.put('/api/auth/user', { name });
            updateUser({ ...user, ...response.data });
            setProfileFeedback({ severity: 'success', message: 'Dados atualizados.' });
        } catch (err) {
            setProfileFeedback({ severity: 'error', message: apiError(err, 'Erro ao salvar os dados.') });
        } finally {
            setSavingProfile(false);
        }
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setPasswordFeedback({ severity: 'error', message: 'A confirmação não confere com a nova senha.' });
            return;
        }
        try {
            setSavingPassword(true);
            setPasswordFeedback(null);
            await api.put('/api/auth/user', { password: newPassword, current_password: currentPassword });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setPasswordFeedback({ severity: 'success', message: 'Senha alterada.' });
            if (user.must_change_password) {
                // Libera o restante do sistema (as rotas ficam presas no perfil até a troca)
                updateUser({ ...user, must_change_password: false });
                const isManager = user.role === 'Gestor' || user.role === 'Diretor';
                navigate(isManager ? '/gestor/dashboard' : '/dashboard', { replace: true });
            }
        } catch (err) {
            setPasswordFeedback({ severity: 'error', message: apiError(err, 'Erro ao alterar a senha.') });
        } finally {
            setSavingPassword(false);
        }
    };

    const companyName = (user as any).company?.name;

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h5" gutterBottom>Meu perfil</Typography>
            {user.must_change_password && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Primeiro acesso: defina uma nova senha para continuar. A senha atual é a senha temporária que você recebeu.
                </Alert>
            )}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card elevation={0}>
                        <CardHeader title="Dados da conta" titleTypographyProps={{ variant: 'h6' }} />
                        <CardContent component="form" onSubmit={handleSaveProfile}>
                            {profileFeedback && <Alert severity={profileFeedback.severity} sx={{ mb: 2 }}>{profileFeedback.message}</Alert>}
                            <TextField fullWidth margin="normal" label="Nome" value={name} onChange={(e) => setName(e.target.value)} required />
                            <TextField fullWidth margin="normal" label="E-mail" value={user.email} disabled />
                            <TextField fullWidth margin="normal" label="Perfil de acesso" value={user.role} disabled
                                helperText="Alterado apenas por um administrador" />
                            {companyName && <TextField fullWidth margin="normal" label="Empresa" value={companyName} disabled />}
                            <Box mt={2}>
                                <Button type="submit" variant="contained" disabled={savingProfile || !name.trim() || name.trim() === user.name}>
                                    Salvar
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card elevation={0}>
                        <CardHeader title="Alterar senha" titleTypographyProps={{ variant: 'h6' }} />
                        <CardContent component="form" onSubmit={handleChangePassword}>
                            {passwordFeedback && <Alert severity={passwordFeedback.severity} sx={{ mb: 2 }}>{passwordFeedback.message}</Alert>}
                            <TextField fullWidth margin="normal" type="password" label="Senha atual" autoComplete="current-password"
                                value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
                            <TextField fullWidth margin="normal" type="password" label="Nova senha" autoComplete="new-password"
                                value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                            {newPassword && <PasswordStrengthIndicator strength={calculatePasswordStrength(newPassword)} />}
                            <TextField fullWidth margin="normal" type="password" label="Confirmar nova senha" autoComplete="new-password"
                                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                                error={!!confirmPassword && confirmPassword !== newPassword}
                                helperText={confirmPassword && confirmPassword !== newPassword ? 'Não confere com a nova senha' : ' '} />
                            <Box mt={1}>
                                <Button type="submit" variant="contained"
                                    disabled={savingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword}>
                                    Alterar senha
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ProfilePage;
